import styled from '@emotion/styled';
import { differenceInSeconds } from 'date-fns';
import { Fragment, useRef } from 'react';
import { mergeProps, useButton, useFocusRing, useHover } from 'react-aria';

import { Glyph } from '../common/Glyph';
import { ImageBase } from '../common/ImageBase';
import { TextTiny } from '../common/text/TextTiny';

import { useEventCallback } from '../../hooks/useEventCallback';
import { useI18n } from '../../hooks/useI18nFormatters';

import { su } from '../../theme/functions/su';
import { glyph } from '../../theme/glyph';
import { HomeyImages } from '../../theme/HomeyImages';
import { motion } from '../../theme/motion';
import { theme } from '../../theme/theme';

export function NotificationsListItem(props) {
  const { i18n } = useI18n();
  const hover = useHover({});
  const deleteButtonRef = useRef(null);

  const handleDeletePress = useEventCallback(() => {
    props.onRequestDelete({ id: props.notification.id });
  });

  const deleteButton = useButton(
    {
      'aria-label': i18n.messageFormatter('common.delete'),
      onPress: handleDeletePress,
    },
    deleteButtonRef,
  );
  const deleteFocusRing = useFocusRing();

  const avatarSrc = props.notification.meta?.userAthomId
    ? `${import.meta.env.VITE_ATHOM_CLOUD_API_BASEURL || 'https://api.athom.com'}/user/${
        props.notification.meta.userAthomId
      }/avatar?size=medium`
    : null;

  const date = new Date(props.notification.dateCreated);

  function formatDate(date) {
    if (hover.isHovered) {
      return i18n.dateFormatter.format(date, 'MMM dd yyyy HH:mm:ss');
    }

    if (differenceInSeconds(new Date(), date) <= 60) {
      return i18n.dateFormatter.formatDistanceToNow(date, {
        addSuffix: true,
      });
    }

    return i18n.dateFormatter.formatDistanceToNowStrict(date, {
      addSuffix: true,
    });
  }

  return (
    <TimelineItemContainer {...hover.hoverProps} data-is-hovered={hover.isHovered}>
      {avatarSrc ? (
        <IconMaskContainer>
          <Image round url={avatarSrc} />
        </IconMaskContainer>
      ) : (
        <IconMaskContainer>
          <Glyph
            style={{
              zIndex: 2,
            }}
            size={theme.icon.size_medium}
            color={theme.color.icon_dark}
            source={HomeyImages.getIconForUri(props.notification.ownerUri)}
          />
        </IconMaskContainer>
      )}

      <Content>
        <Excerpt>{mapExcerpt(props.notification?.excerpt ?? '')}</Excerpt>
        <TextTiny lineHeight={1.167} color={theme.color.text_light}>
          {formatDate(date)}
        </TextTiny>
      </Content>

      <DeleteAction
        {...mergeProps(deleteButton.buttonProps, deleteFocusRing.focusProps)}
        ref={deleteButtonRef}
        data-is-pressed={deleteButton.isPressed}
        data-is-focus-visible={deleteFocusRing.isFocusVisible}
      >
        <DeleteCircle aria-hidden="true">
          <Glyph
            size={theme.icon.size_small}
            color={theme.color.component}
            source={glyph.core.trashFill}
          />
        </DeleteCircle>
      </DeleteAction>
    </TimelineItemContainer>
  );
}

function mapExcerpt(excerpt) {
  return excerpt?.split?.('**').map((part, index) => {
    if (index % 2 === 1) {
      return <strong key={index}>{part}</strong>;
    }

    return <Fragment key={index}>{part}</Fragment>;
  });
}

const DeleteCircle = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${su(3)};
  height: ${su(3)};
  border-radius: 50%;
  background-color: ${theme.color.icon_light};
  transition: all ${motion.duration.fast} ${motion.curve.fast_in};
`;

const DeleteAction = styled.button`
  opacity: 0;
  // Margin instead of padding keeps the hit area limited to the visible circle.
  margin: ${su(1)};
  padding: 0;
  border: 0;
  border-radius: 50%;
  background-color: transparent;
  cursor: pointer;

  transition: all ${motion.duration.fast} ${motion.curve.fast_in};

  &[data-is-focus-visible='true'] {
    opacity: 1;
    outline: 2px solid ${theme.color.focus_ring};
    outline-offset: 2px;
  }

  &:hover,
  &[data-is-pressed='true'] {
    ${DeleteCircle} {
      background-color: ${theme.color.danger};
    }
  }
`;

const Content = styled.div`
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  opacity: ${(props) => {
    return props.isRead ? 0.5 : 1;
  }};
`;

const IconMaskContainer = styled.div`
  display: flex;
  z-index: 2;
  align-self: flex-start;
  align-items: center;
  justify-content: center;
  min-width: ${su(6)};
  max-width: ${su(6)};
  min-height: ${su(6)};
  max-height: ${su(6)};
  background-color: ${theme.color.component};
  margin-inline: ${su(1)} ${su(2)};
  border: 2px solid ${theme.color.line};
  border-radius: 50%;
`;

const Image = styled(ImageBase)`
  z-index: 2;
  min-width: 44px;
  max-width: 44px;
  min-height: 44px;
  max-height: 44px;
`;

const Excerpt = styled.div`
  word-break: break-word;
  white-space: pre-wrap;
`;

const TimelineItemContainer = styled.div`
  display: flex;
  position: relative;
  align-items: center;
  min-height: ${su(6)};
  padding: ${su(1)};
  padding-inline-end: 0;
  transition: ${motion.transition.micro};

  // CSS hover also updates when a deletion moves this row under a stationary pointer.
  &:hover,
  &[data-is-hovered='true'] {
    ${DeleteAction} {
      opacity: 1;
    }
  }

  :not(:last-child):after {
    z-index: 1;
    content: '';
    position: absolute;
    top: 58px;
    inset-inline-start: 40px;
    height: 100%;
    overflow: visible;
    width: 2px;
    background-color: ${theme.color.line};
  }
`;
